import React, { useState } from 'react';
import { Input, Button, Grid, Dropdown, Label, Icon, Segment, Divider } from 'semantic-ui-react';
import AdvancedFilterPanel from './AdvancedFilterPanel';

/**
 * Enhanced Filter Panel component with advanced search and filtering capabilities
 * Backward compatible with basic filters, supports advanced features when provided
 */
const FilterPanel = ({
    // Basic filters (backward compatible)
    statusFilter = '',
    cloudNameFilter = '',
    categoryFilter = '',
    requestIdFilter = '',
    // Advanced filters (optional)
    globalSearch,
    selectedStatuses,
    selectedCategories,
    dateRangeStart,
    dateRangeEnd,
    submittedByFilter,
    // Saved filters (optional)
    savedFilters,
    filterPresetName,
    availableStatuses = [],
    availableCategories = [],
    // Callbacks
    onStatusFilterChange,
    onCloudNameFilterChange,
    onCategoryFilterChange,
    onRequestIdFilterChange,
    onGlobalSearchChange,
    onSelectedStatusesChange,
    onSelectedCategoriesChange,
    onDateRangeStartChange,
    onDateRangeEndChange,
    onSubmittedByFilterChange,
    onFilterPresetNameChange,
    onSaveFilterPreset,
    onLoadFilterPreset,
    onDeleteFilterPreset,
    onResetFilters,
    hasActiveFilters,
    onFilterChange,
    // Mode: 'basic' or 'advanced'
    mode = 'advanced',
    // Close handler for sidebar
    onClose
}) => {
    const [showAdvanced, setShowAdvanced] = useState(false);

    const handleFilterChange = (setter, value) => {
        if (setter) {
            setter(value);
        }
        if (onFilterChange) {
            onFilterChange();
        }
    };

    // If advanced mode and all advanced props are provided, use AdvancedFilterPanel
    if (mode === 'advanced' && onGlobalSearchChange) {
        return (
            <AdvancedFilterPanel
                statusFilter={statusFilter}
                cloudNameFilter={cloudNameFilter}
                categoryFilter={categoryFilter}
                requestIdFilter={requestIdFilter}
                globalSearch={globalSearch}
                selectedStatuses={selectedStatuses}
                selectedCategories={selectedCategories}
                dateRangeStart={dateRangeStart}
                dateRangeEnd={dateRangeEnd}
                submittedByFilter={submittedByFilter}
                savedFilters={savedFilters}
                filterPresetName={filterPresetName}
                availableStatuses={availableStatuses}
                availableCategories={availableCategories}
                onStatusFilterChange={onStatusFilterChange}
                onCloudNameFilterChange={onCloudNameFilterChange}
                onCategoryFilterChange={onCategoryFilterChange}
                onRequestIdFilterChange={onRequestIdFilterChange}
                onGlobalSearchChange={onGlobalSearchChange}
                onSelectedStatusesChange={onSelectedStatusesChange}
                onSelectedCategoriesChange={onSelectedCategoriesChange}
                onDateRangeStartChange={onDateRangeStartChange}
                onDateRangeEndChange={onDateRangeEndChange}
                onSubmittedByFilterChange={onSubmittedByFilterChange}
                onFilterPresetNameChange={onFilterPresetNameChange}
                onSaveFilterPreset={onSaveFilterPreset}
                onLoadFilterPreset={onLoadFilterPreset}
                onDeleteFilterPreset={onDeleteFilterPreset}
                onResetFilters={onResetFilters}
                hasActiveFilters={hasActiveFilters}
                onFilterChange={onFilterChange}
                onClose={onClose}
            />
        );
    }

    // Basic mode - backward compatible
    return (
        <div style={{
            marginBottom: '16px',
            padding: '12px',
            background: '#f9fafb',
            borderRadius: '4px',
            border: '1px solid #e1e8ed',
            zIndex: 99,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
            <Grid columns="equal" stackable>
                <Grid.Row>
                    <Grid.Column>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9em', fontWeight: 'bold' }}>
                            Status
                        </label>
                        <Input
                            placeholder="Search by status..."
                            value={statusFilter || ''}
                            onChange={(e) => handleFilterChange(onStatusFilterChange, e.target.value)}
                            icon="search"
                            style={{ 
                                width: '100%',
                                borderColor: statusFilter ? '#062D4D' : undefined,
                                boxShadow: statusFilter ? '0 0 0 1px #062D4D' : undefined
                            }}
                            className={statusFilter ? 'tb-filter-active' : ''}
                        />
                    </Grid.Column>
                    <Grid.Column>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9em', fontWeight: 'bold' }}>
                            Cloud Name
                        </label>
                        <Input
                            placeholder="Search by cloud name..."
                            value={cloudNameFilter || ''}
                            onChange={(e) => handleFilterChange(onCloudNameFilterChange, e.target.value)}
                            icon="search"
                            style={{ 
                                width: '100%',
                                borderColor: cloudNameFilter ? '#062D4D' : undefined,
                                boxShadow: cloudNameFilter ? '0 0 0 1px #062D4D' : undefined
                            }}
                            className={cloudNameFilter ? 'tb-filter-active' : ''}
                        />
                    </Grid.Column>
                    <Grid.Column>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9em', fontWeight: 'bold' }}>
                            Category
                        </label>
                        <Input
                            placeholder="Search by category..."
                            value={categoryFilter || ''}
                            onChange={(e) => handleFilterChange(onCategoryFilterChange, e.target.value)}
                            icon="search"
                            style={{ 
                                width: '100%',
                                borderColor: categoryFilter ? '#062D4D' : undefined,
                                boxShadow: categoryFilter ? '0 0 0 1px #062D4D' : undefined
                            }}
                            className={categoryFilter ? 'tb-filter-active' : ''}
                        />
                    </Grid.Column>
                    <Grid.Column>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9em', fontWeight: 'bold' }}>
                            Request ID
                        </label>
                        <Input
                            placeholder="Search by request ID..."
                            value={requestIdFilter || ''}
                            onChange={(e) => handleFilterChange(onRequestIdFilterChange, e.target.value)}
                            icon="search"
                            style={{ 
                                width: '100%',
                                borderColor: requestIdFilter ? '#062D4D' : undefined,
                                boxShadow: requestIdFilter ? '0 0 0 1px #062D4D' : undefined
                            }}
                            className={requestIdFilter ? 'tb-filter-active' : ''}
                        />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column>
                        <Button
                            onClick={() => {
                                if (onResetFilters) onResetFilters();
                                if (onFilterChange) {
                                    onFilterChange();
                                }
                            }}
                            disabled={!hasActiveFilters}
                            style={{ width: '100%' }}
                        >
                            Clear Filters
                        </Button>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </div>
    );
};

export default FilterPanel;

